import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { caseKey, type CaseDetail, type CaseNote } from "../caseApi";

const AUTOSAVE_MS = 800;

/**
 * The rep's working note for a case (their latest one; the first save creates it). It saves 800 ms after the last
 * keystroke. A 409 means someone saved first: the newer text is shown and the rep keeps theirs or takes the newer.
 * Other people's notes are listed read-only below.
 */
export function NotesPanel({ caseId, notes: given, readOnly = false }: { caseId: string; notes?: CaseNote[]; readOnly?: boolean }) {
  const notes = given ?? [];
  const { me } = useAuth();
  const qc = useQueryClient();
  // The notes list is newest first from the API, but pick explicitly: my most recently updated note.
  const mine = notes.filter((n) => n.userId === me?.user.id).sort((x, y) => (x.updatedAt < y.updatedAt ? 1 : -1))[0];
  const [text, setText] = useState(mine?.body ?? "");
  const [saved, setSaved] = useState(mine?.body ?? "");
  const [noteId, setNoteId] = useState<string | null>(mine?.id ?? null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(mine?.updatedAt ?? null);
  const [newer, setNewer] = useState<CaseNote | null>(null);
  const [failed, setFailed] = useState(false);
  const busy = useRef(false);
  const alive = useRef(true);
  // The latest values, for the save that runs when the panel unmounts.
  const latest = useRef({ text, saved, noteId, updatedAt, newer });
  const ro = useRef(readOnly);
  ro.current = readOnly;
  latest.current = { text, saved, noteId, updatedAt, newer };

  const save = async () => {
    const { text: sent, noteId: id, updatedAt: at } = latest.current;
    const body = sent.trim();
    if (!body) return; // the server refuses an empty note
    busy.current = true;
    try {
      const res = id && at
        ? await api<{ note: CaseNote }>(`/api/cases/${caseId}/notes/${id}`, { method: "PUT", body: { body, updatedAt: at } })
        : await api<{ note: CaseNote }>(`/api/cases/${caseId}/notes`, { method: "POST", body: { body } });
      // The cache is updated even after the panel is gone, so coming back within the stale time shows this text and updatedAt.
      qc.setQueryData<CaseDetail>(caseKey(caseId), (d) => d && ({
        ...d, notes: d.notes.some((n) => n.id === res.note.id)
          ? d.notes.map((n) => (n.id === res.note.id ? { ...n, ...res.note } : n))
          : [{ ...res.note, authorName: me?.user.name }, ...d.notes],
      }));
      qc.invalidateQueries({ queryKey: caseKey(caseId) });
      if (!alive.current) return;
      setNoteId(res.note.id);
      setUpdatedAt(res.note.updatedAt);
      setSaved(sent);
      setFailed(false);
    } catch (e) {
      if (!alive.current) return;
      const current = (e instanceof ApiError && e.status === 409 ? (e.details as { note?: CaseNote } | undefined)?.note : undefined);
      if (current) setNewer(current);
      else setFailed(true);
    } finally {
      busy.current = false;
    }
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  // Debounced autosave. A failure waits for the next keystroke; a conflict waits for the rep's choice.
  useEffect(() => {
    if (readOnly || newer || failed || busy.current || !text.trim() || text === saved) return;
    const t = setTimeout(() => saveRef.current(), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [text, saved, newer, failed, readOnly]);

  const dirty = !readOnly && text !== saved;
  // Leaving the screen (route change) sends an edit still waiting for its debounce; closing the tab asks first.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  useEffect(() => () => {
    const l = latest.current;
    if (!ro.current && !l.newer && !busy.current && l.text.trim() && l.text !== l.saved) void saveRef.current();
    alive.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (v: string) => { setText(v); setFailed(false); };
  const keepMine = () => { if (newer) { setUpdatedAt(newer.updatedAt); setNoteId(newer.id); setNewer(null); } };
  const takeNewer = () => {
    if (!newer) return;
    setText(newer.body); setSaved(newer.body); setNoteId(newer.id); setUpdatedAt(newer.updatedAt); setNewer(null);
  };
  const inSync = text === saved;
  const empty = !text.trim() && !!saved.trim();

  return (
    <section className="card" aria-label="Notes">
      <div className="mb-2 flex items-center justify-between">
        <label htmlFor="case-note" className="label mb-0">Case note</label>
        <span className="text-xs text-slate-500" role="status">{inSync && !newer && !failed ? "Saved" : empty ? "Not saved yet: notes can't be empty" : "Not saved yet"}</span>
      </div>
      {newer && (
        <div role="alert" className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950">
          <p className="font-semibold">Someone else saved a newer version of this note.</p>
          <p className="mt-1 whitespace-pre-wrap text-slate-700 dark:text-slate-300">{newer.body}</p>
          <div className="mt-2 flex gap-2">
            <button type="button" className="btn-primary" onClick={keepMine}>Keep mine</button>
            <button type="button" className="btn-ghost" onClick={takeNewer}>Take newer</button>
          </div>
        </div>
      )}
      {failed && <p role="alert" className="mb-2 text-sm text-red-600">Could not save the note. It will retry when you type.</p>}
      <textarea id="case-note" aria-label="Case note" className="input min-h-40" readOnly={readOnly} value={text}
        onChange={(e) => onChange(e.target.value)} placeholder="What the caller said, what you told them…" />
      {notes.filter((n) => n.id !== noteId).length > 0 && (
        <ul className="mt-3 space-y-2 text-sm">
          {notes.filter((n) => n.id !== noteId).map((n) => (
            <li key={n.id} className="rounded-md border border-slate-200 p-2 dark:border-slate-800">
              <div className="text-xs text-slate-500">{n.authorName ?? "Rep"} · {new Date(n.updatedAt).toLocaleString()}</div>
              <div className="whitespace-pre-wrap">{n.body}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
